import { Grid, Divider, Tabs, Tab, Skeleton } from '@mui/material';
import { Stack } from '@mui/system';

import { FieldArray, useFormikContext } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormError } from 'app/components/FormError/FormError';
import { translations } from 'locales/i18n';
import { Company, Faculty } from 'types/AppTypes';

import CompanyDescription from './CompanyDescription';
import CompanyInformation from './CompanyInformation';
import CompanyInvoice from './CompanyInvoice';
import ContactInformation from './ContactInformation';
import FacultyBox from './FacultyBox';
import TabPanel from './TabPanel';

interface Props {
  company: Company;
  faculties: Faculty[];
  editMode?: boolean;
  newCompany?: boolean;
}

const CompanyDetailBody = (props: Props) => {
  const { t } = useTranslation();
  const formik = useFormikContext<Company>();
  const [value, setValue] = useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  // ! Add new fields of tabs here -> show error in tab
  const tabs = {
    companyTab: ['name', 'street', 'zip', 'city', 'country', 'phone', 'fax', 'homepage', 'email'],
    invoiceTab: [
      'invoiceName',
      'invoiceStreet',
      'invoiceZip',
      'invoiceCity',
      'invoiceCountry',
      'invoiceEmail',
      'taxId',
      'taxNumber'
    ]
  };

  const readOnlyFields = () => (
    <div className="company-detail__body">
      <Grid container>
        <Grid item md={4}>
          <Grid container style={{ width: '95%', border: '1px solid lightgray' }}>
            <Tabs
              value={value}
              indicatorColor="primary"
              textColor="primary"
              onChange={handleChange}
              variant="fullWidth"
              style={{
                width: '100%'
              }}
            >
              <Tab label={t(translations.pages.manageCompanies.company())}></Tab>
              <Tab label={t(translations.pages.manageCompanies.invoice())}></Tab>
            </Tabs>
            <TabPanel value={value} index={0}>
              <CompanyInformation editMode={props.editMode} company={props.company} />
            </TabPanel>
            <TabPanel value={value} index={1}>
              <CompanyInvoice editMode={props.editMode} company={props.company} />
            </TabPanel>
          </Grid>
          <ContactInformation
            editMode={props.editMode}
            contact={props.company.mainContact}
            newCompany={props.newCompany}
          />
          <CompanyDescription editMode={props.editMode} company={props.company} />
        </Grid>
        <Divider orientation="vertical" flexItem className="divider" />
        <Grid container item md className="section" alignContent="flex-start">
          <Grid container className="section__header">
            {t(translations.pages.manageCompanies.faculties())}
          </Grid>
          {props.faculties.map(f => (
            <FacultyBox
              editMode={props.editMode}
              key={f.id}
              faculty={f}
              selectedCourses={props.company.courses}
            />
          ))}
        </Grid>
      </Grid>
    </div>
  );

  if (!props.editMode) {
    return readOnlyFields();
  }

  const { errors } = formik;

  return (
    <div className="company-detail__body">
      <Grid container>
        <Grid item md={4}>
          <Grid container style={{ width: '95%', border: '1px solid lightgray' }}>
            <Tabs
              value={value}
              indicatorColor="primary"
              textColor="primary"
              onChange={handleChange}
              variant="fullWidth"
              style={{ width: '100% ' }}
            >
              <Tab
                label={t(translations.pages.manageCompanies.company())}
                style={{
                  color: Object.keys(errors).find(error => tabs.companyTab.includes(error))
                    ? 'red'
                    : undefined,
                  fontWeight: Object.keys(errors).find(error => tabs.companyTab.includes(error))
                    ? 900
                    : undefined
                }}
              ></Tab>
              <Tab
                label={t(translations.pages.manageCompanies.invoice())}
                style={{
                  color: Object.keys(errors).find(error => tabs.invoiceTab.includes(error))
                    ? 'red'
                    : undefined,
                  fontWeight: Object.keys(errors).find(error => tabs.invoiceTab.includes(error))
                    ? 900
                    : undefined
                }}
              ></Tab>
            </Tabs>
            <TabPanel value={value} index={0}>
              <CompanyInformation editMode={props.editMode} company={props.company} />
            </TabPanel>
            <TabPanel value={value} index={1}>
              <CompanyInvoice editMode={props.editMode} company={props.company} />
            </TabPanel>
          </Grid>
          <ContactInformation editMode={props.editMode} contact={props.company.mainContact} />
          <CompanyDescription editMode={props.editMode} company={props.company} />
        </Grid>
        <Divider orientation="vertical" flexItem className="divider" />
        <Grid container item md className="section" alignContent="flex-start">
          <Grid container className="section__header">
            {t(translations.pages.manageCompanies.faculties())}
          </Grid>
          {errors.courses && (
            <Grid container className="section__error">
              <FormError message={errors.courses.toString()} />
            </Grid>
          )}
          <FieldArray name="courses">
            {({ form, ...helpers }) =>
              props.faculties.length ? (
                props.faculties.map(f => (
                  <FacultyBox
                    editMode={props.editMode}
                    key={f.id}
                    faculty={f}
                    selectedCourses={form.values.courses}
                    arrayHelpers={helpers}
                  />
                ))
              ) : (
                <Stack spacing={2}>
                  {[...Array(6)].map((_e, i) => (
                    <Skeleton variant="rounded" width={700} height={110} key={i} />
                  ))}
                </Stack>
              )
            }
          </FieldArray>
        </Grid>
      </Grid>
    </div>
  );
};

export default CompanyDetailBody;
